import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';

import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError, first } from 'rxjs/operators';
import { LifecycleEventStatus } from 'src/app/lifecycle-events/models/lifecycle-event-status.enum';
import { DcAlertService } from 'src/app/ui-kit/dc-alert/dc-alert.service';
import { ModalService } from 'src/app/ui-kit/dc-modal/modal.service';
import { PageData } from '../model/interfaces/page-data';
import { ProductCaptureService } from '../services/product-capture.service';
import { GetErrorMessageFromResponse } from '../utils/get-error-message-from-response';
import { AuditModalService } from './audit-modal/audit-modal.service';
import { TerminateModalComponent } from './terminate-modal/terminate-modal.component';

@Component({
  selector: 'app-product-capture-list',
  templateUrl: './product-capture-list.component.html',
  styleUrls: ['./product-capture-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductCaptureListComponent implements OnInit {
  @Input() rows;
  @Input() loading;
  @Input() pageData: PageData;
  @Output() deleteRow = new EventEmitter();
  @Output() onPageChange = new EventEmitter();

  columns;
  sortOrder = [
    {
      prop: 'createdOn',
      dir: 'desc',
    },
  ];
  messages = {
    emptyMessage: 'No products found.',
    totalMessage: 'Total',
  };
  showFooter = false;

  constructor(
    private modalService: ModalService,
    private router: Router,
    private auditModalService: AuditModalService,
    private productCaptureService: ProductCaptureService,
    private dcAlertService: DcAlertService,
  ) {}

  ngOnInit(): void {
    this.columns = [
      {
        name: 'Created On',
        prop: 'createdOn',
        resizeable: false,
        draggable: false,
        minWidth: 140,
        maxWidth: 140,
        canAutoResize: true,
        flexGrow: 1,
        cellTemplate: 'dateTimeTemplateNoGmt',
      },
      {
        name: 'Updated On',
        prop: 'updatedOn',
        resizeable: false,
        draggable: false,
        minWidth: 140,
        maxWidth: 140,
        canAutoResize: true,
        flexGrow: 1,
        cellTemplate: 'dateTimeTemplateNoGmt',
      },
      {
        name: 'ISIN',
        prop: 'isin',
        resizeable: false,
        draggable: false,
        minWidth: 100,
        maxWidth: 120,
        canAutoResize: true,
        flexGrow: 1,
        cellTemplate: 'valueOrDashTemplate',
        cellClass: 'isinCell',
      },
      {
        name: 'Product Name',
        prop: 'productName',
        resizeable: false,
        draggable: false,
        minWidth: 160,
        flexGrow: 3,
        canAutoResize: true,
        cellTemplate: 'valueOrDashTemplate',
        cellClass: 'productNameCell',
      },
      {
        name: 'Tenor',
        prop: 'tenor',
        headerClass: '',
        cellClass: '',
        resizeable: false,
        draggable: false,
        width: 60,
        maxWidth: 60,
        flexGrow: 1,
        canAutoResize: false,
        cellTemplate: 'valueOrDashTemplate',
      },
      {
        name: 'CCY',
        prop: 'currency',
        headerClass: '',
        cellClass: '',
        resizeable: false,
        draggable: false,
        width: 50,
        maxWidth: 50,
        flexGrow: 1,
        canAutoResize: false,
        cellTemplate: 'valueOrDashTemplate',
      },
      {
        name: 'Notional',
        prop: 'notional',
        headerClass: '',
        cellClass: '',
        resizeable: false,
        draggable: false,
        width: 140,
        maxWidth: 140,
        flexGrow: 1,
        canAutoResize: false,
        cellTemplate: 'numberTemplate',
      },
      {
        name: 'Issue Date',
        prop: 'issueDate',
        headerClass: '',
        cellClass: '',
        resizeable: false,
        draggable: false,
        width: 80,
        maxWidth: 80,
        flexGrow: 1,
        canAutoResize: false,
        cellTemplate: 'dateTemplateNoGmt',
      },
      {
        name: 'Redemption',
        prop: 'maturityDate',
        headerClass: '',
        cellClass: '',
        resizeable: false,
        draggable: false,
        width: 80,
        maxWidth: 80,
        flexGrow: 1,
        canAutoResize: false,
        cellTemplate: 'dateTemplateNoGmt',
      },
      {
        name: 'Created By',
        prop: 'createdBy',
        resizeable: false,
        draggable: false,
        minWidth: 120,
        maxWidth: 120,
        canAutoResize: true,
        flexGrow: 1,
        cellTemplate: 'reformattedAWSUsernameTemplate',
      },
      {
        name: 'Updated By',
        prop: 'lastUpdatedBy',
        resizeable: false,
        draggable: false,
        minWidth: 120,
        maxWidth: 120,
        canAutoResize: true,
        flexGrow: 1,
        cellTemplate: 'reformattedAWSUsernameTemplate',
      },
      {
        name: 'Status',
        prop: 'recordStatus',
        resizeable: false,
        draggable: false,
        minWidth: 80,
        maxWidth: 80,
        canAutoResize: true,
        flexGrow: 1,
        cellTemplate: 'titlecaseTemplate',
      },
      {
        name: 'Lifecycle Status',
        prop: 'lifecycleStatus',
        resizeable: false,
        draggable: false,
        minWidth: 120,
        maxWidth: 120,
        canAutoResize: true,
        flexGrow: 1,
        cellTemplate: 'titlecaseTemplate',
      },
      {
        name: 'Action',
        prop: 'productId',
        headerClass: 'text-center',
        cellClass: 'text-center actionButtonCell',
        resizeable: false,
        draggable: false,
        flexGrow: 1,
        canAutoResize: true,
        maxWidth: 100,
        cellTemplate: 'productActionButtonTemplate',
      },
    ];
  }

  tableAction(action) {
    switch (action.type) {
      case 'view':
        this.router.navigate([
          '/product-capture/view-details',
          action.row.productId,
        ]);
        break;
      case 'auditView':
        this.auditModalService.openDialog({
          productId: action.row.productId,
          productName: action.row.productName,
        });
        break;
      case 'clone':
        const cloneTitle = 'Clone ' + action.row.productName;
        const cloneBody =
          '<p>Are you sure you want to clone ' +
          action.row.productName +
          '.</p>';
        this.modalService
          .openDialog({
            title: cloneTitle,
            body: cloneBody,
            buttons: {
              positive: 'Yes',
              negative: 'No',
            },
            size: 'md',
          })
          .then(
            (result) => {
              if (result == '{"positive":1}') {
                this.router.navigate([
                  '/product-capture/create-product',
                  action.row.productId,
                ]);
              }
            },
            (error) => {
              console.log(error);
            }
          );
        break;
      case 'edit':
        const editTitle = 'Edit ' + action.row.productName;
        const editBody =
          '<p>Are you sure you want to edit ' +
          action.row.productName +
          '.</p>';
        this.modalService
          .openDialog({
            title: editTitle,
            body: editBody,
            buttons: {
              positive: 'Yes',
              negative: 'No',
            },
            size: 'md',
          })
          .then(
            (result) => {
              if (result == '{"positive":1}') {
                this.router.navigate([
                  '/product-capture/edit-product',
                  action.row.productId,
                ]);
              }
            },
            (error) => {
              console.log(error);
            }
          );
        break;
      case 'delete':
        const deleteTitle = 'Delete ' + action.row.productName;
        const deleteBody =
          '<p>Are you sure you want to delete ' +
          action.row.productName +
          '.</p>';
        this.modalService
          .openDialog({
            title: deleteTitle,
            body: deleteBody,
            buttons: {
              positive: 'Delete',
              negative: 'Cancel',
            },
            size: 'md',
          })
          .then(
            (result) => {
              if (result == '{"positive":1}') {
                this.deleteRow.emit({
                  id: action.row.productId,
                  name: action.row.productName,
                });
              }
            },
            (error) => {
              console.log(error);
            }
          );
        break;
      case 'terminate':
        this.openTerminateConfirmationModal(action);
        break;
      default:
        //
        break;
    }
  }

  tableActivate(e) {
    if (e.type === 'click') {
      if (e.value !== e.row.productId) {
        this.router.navigate([
          '/product-capture/view-details',
          e.row.productId,
        ]);
      }
    }
  }

  openTerminateConfirmationModal(action: any): void {
    const modal = this.modalService.openModal({
      size: 'md',
      onTerminate: (reason: string) => { 
        this.terminateProduct(action, reason);
        modal.close();
      },
    }, TerminateModalComponent);
  }

  terminateProduct(action: any, reason: string): void {
    action.row.loading = true;
    
    this.productCaptureService.changeProductLifecycleStatus(action.row, reason, LifecycleEventStatus.TERMINATED).pipe(
        first(),
        catchError((error: HttpErrorResponse) => {
          action.row.loading = false;
          this.dcAlertService.danger(`Error terminating product: ${GetErrorMessageFromResponse(error)}`);
          return throwError(error);
        })
      )
      .subscribe(() => {
        action.row.loading = false;
        action.row.lifecycleStatus = LifecycleEventStatus.TERMINATED;
        this.dcAlertService.success('Product terminated successfully');
      });
  }

  pageChange(e: any) {
    this.onPageChange.emit(e);
  }
}
